/**
 * Kanal einer Zahlung für die Erstattung einer Gutschrift.
 * Dieselbe Regel steht serverseitig in `api/core/payment_channel.py`.
 */
import { PAYMENT_METHOD_LABELS, type PaymentMethod } from "@/lib/paymentMethod";

export type RefundChannel = "stripe" | "terminal" | "cash" | "card" | "transfer" | "other";

export interface RefundSource {
  payment_method: string;
  reference: string | null;
  notes: string | null;
}

export interface OriginalPayment extends RefundSource {
  id: string;
  amount: string;
  paid_at: string;
}

export const REFUND_CHANNEL_LABELS: Record<RefundChannel, string> = {
  stripe: "Stripe",
  terminal: "EC-Terminal",
  cash: "Bar",
  card: "Karte (manuell)",
  transfer: "Überweisung",
  other: "Sonstiges",
};

export function stripeSessionId(reference: string | null | undefined): string | null {
  if (!reference) return null;
  const head = reference.trim().split(":")[0]?.trim() ?? "";
  if (head.startsWith("cs_") && head.length > 3) return head;
  return null;
}

export function isStripePayment(payment: RefundSource): boolean {
  if (stripeSessionId(payment.reference)) return true;
  return payment.payment_method === "card" && (payment.notes ?? "").toLowerCase().includes("stripe");
}

export function refundChannelOf(payment: RefundSource): RefundChannel {
  if (isStripePayment(payment)) return "stripe";
  if (payment.payment_method === "card") {
    return (payment.reference ?? "").trim() ? "terminal" : "card";
  }
  if (
    payment.payment_method === "cash" ||
    payment.payment_method === "transfer" ||
    payment.payment_method === "other"
  ) {
    return payment.payment_method;
  }
  return "other";
}

export function refundChannelLabel(channel: RefundChannel): string {
  return REFUND_CHANNEL_LABELS[channel];
}

export function payoutMethodLabel(payment: RefundSource): string {
  const notes = (payment.notes ?? "").toLowerCase();
  const reference = (payment.reference ?? "").trim();
  if (notes.includes("stripe") || reference.startsWith("re_") || reference.startsWith("cs_")) {
    return "Stripe";
  }
  if (payment.payment_method === "card" && (notes.includes("beleg") || notes.includes("karte:"))) {
    return "EC-Terminal";
  }
  const key = payment.payment_method as PaymentMethod;
  return PAYMENT_METHOD_LABELS[key] ?? payment.payment_method;
}

export function paymentMethodForChannel(channel: RefundChannel): PaymentMethod {
  if (channel === "stripe" || channel === "terminal" || channel === "card") return "card";
  if (channel === "cash" || channel === "transfer" || channel === "other") return channel;
  return "other";
}

/** Größte Zahlung, bei gleichem Betrag die jüngste. */
export function preferredOriginalPayment<T extends { amount: string; paid_at: string }>(
  payments: T[],
): T | null {
  let best: T | null = null;
  for (const payment of payments) {
    if (!best) {
      best = payment;
      continue;
    }
    const amount = Number(payment.amount);
    const bestAmount = Number(best.amount);
    if (amount > bestAmount + 0.001) {
      best = payment;
      continue;
    }
    if (Math.abs(amount - bestAmount) <= 0.001 && payment.paid_at > best.paid_at) {
      best = payment;
    }
  }
  return best;
}

export function recommendedPayoutAmount(
  channel: RefundChannel,
  originalAmount: number | null,
  openAmount: number,
): string {
  if (!Number.isFinite(openAmount) || openAmount <= 0) return "";
  if (
    (channel === "stripe" || channel === "terminal") &&
    originalAmount != null &&
    Number.isFinite(originalAmount) &&
    originalAmount > 0
  ) {
    return Math.min(openAmount, originalAmount).toFixed(2);
  }
  return openAmount.toFixed(2);
}

export function availableRefundChannels(payments: RefundSource[]): RefundChannel[] {
  const present = new Set(payments.map(refundChannelOf));
  const channels: RefundChannel[] = ["cash"];
  if (present.has("stripe")) channels.push("stripe");
  if (present.has("terminal")) channels.push("terminal");
  channels.push("card", "transfer", "other");
  return channels;
}

export interface InitialRefundChoice {
  channel: RefundChannel;
  paymentId: string;
  amount: string;
  fallbackNote: string | null;
}

export function initialRefundChoice(
  payments: OriginalPayment[],
  openAmount: number,
  options: { zvtReady: boolean; stripeReady: boolean },
): InitialRefundChoice {
  const preferred = preferredOriginalPayment(payments);
  if (!preferred) {
    return {
      channel: "cash",
      paymentId: "",
      amount: openAmount > 0 ? openAmount.toFixed(2) : "",
      fallbackNote: null,
    };
  }

  const preferredChannel = refundChannelOf(preferred);
  const original = Number(preferred.amount);
  let channel: RefundChannel = preferredChannel;
  let fallbackNote: string | null = null;
  if (preferredChannel === "stripe" && !options.stripeReady) {
    channel = "cash";
    fallbackNote = "Stripe ist nicht konfiguriert. Vorausgewählt ist deshalb Bar.";
  } else if (preferredChannel === "terminal" && !options.zvtReady) {
    channel = "cash";
    fallbackNote = "Das EC-Terminal ist nicht konfiguriert. Vorausgewählt ist deshalb Bar.";
  } else if (preferredChannel === "terminal" && Number.isFinite(original) && original > openAmount + 0.001) {
    channel = "cash";
    fallbackNote =
      "Das EC-Terminal storniert nur den vollen Kartenbetrag, der über dem offenen Rest liegt. Vorausgewählt ist deshalb Bar.";
  }

  const linked = channel === "stripe" || channel === "terminal" ? preferred : null;
  return {
    channel,
    paymentId: linked?.id ?? "",
    amount: recommendedPayoutAmount(channel, linked ? original : null, openAmount),
    fallbackNote,
  };
}
